import { computed, inject, Injectable, signal } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { LoaderStore } from '../../../stores/loader.store';
import { Cluster } from '../../../types/tenant.types';
import { ReportsService } from '../../reports/service/report-service';
import {
  AwaitingClusterFeeRun,
  ClusterFeeRunState,
  UnpaidClusterFeeType,
} from '../model/cluster-fee-run.models';
import { ClusterFeeRunService } from '../service/cluster-fee-run.services';
import * as Sentry from '@sentry/angular';

const initialState: ClusterFeeRunState = {
  unpaidClusterFeeRun: [],
  awaitingClusterFeeRun: [],
  clusters: [],
  approvedClusterFees: [],
  isLoadingCount: 0,
  error: null,
};

@Injectable({ providedIn: 'root' })
export class ClusterFeeRunStore {
  private readonly service = inject(ClusterFeeRunService);
  private readonly loaderStore = inject(LoaderStore);
  private readonly state = signal<ClusterFeeRunState>(initialState);
  private readonly reportService = inject(ReportsService);

  readonly UnpaidClusterFeeRunData = computed(() => this.state().unpaidClusterFeeRun);
  readonly awaitingClusterFeeRun = computed(() => this.state().awaitingClusterFeeRun);
  readonly clustersList = computed(() => this.state().clusters);
  readonly approvedClusterFeesList = computed(() => this.state().approvedClusterFees);

  readonly isLoading = computed(() => this.state().isLoadingCount > 0);
  readonly error = computed(() => this.state().error);

  async loadUnpaidClusterFeeRun(request: {
    statusIds: number[];
    clusterId: number | null;
  }): Promise<UnpaidClusterFeeType[]> {
    this.loaderStore.show();
    this.startLoading();

    try {
      const data = await lastValueFrom(this.service.getUpaidClusterFeeRun(request));
      this.state.update((s) => ({ ...s, unpaidClusterFeeRun: data }));
      return data;
    } catch (err) {
      Sentry.captureException(err);
      this.setError(err);
      return [];
    } finally {
      this.loaderStore.hide();
      this.stopLoading();
    }
  }

  async loadClusterFeeRunAwaitingList(clusterId: number): Promise<AwaitingClusterFeeRun[]> {
    this.loaderStore.show();
    this.startLoading();

    try {
      const data = await lastValueFrom(this.service.getClusterFeeRunAwaitingList(clusterId));
      this.state.update((s) => ({ ...s, awaitingClusterFeeRun: data }));
      return data;
    } catch (err) {
      Sentry.captureException(err);
      this.setError(err);
      return [];
    } finally {
      this.loaderStore.hide();
      this.stopLoading();
    }
  }

  async loadClustersList(): Promise<Cluster[]> {
    this.loaderStore.show();

    try {
      const data = await lastValueFrom(this.service.getClustersList());
      this.state.update((s) => ({ ...s, clusters: data }));
      return data;
    } catch (err) {
      Sentry.captureException(err);
      this.setError(err);
      return [];
    } finally {
      this.loaderStore.hide();
    }
  }

  async closeClusterFeeRun(
    payload: { tenantId: number; fromDate: string; toDate: string }[],
  ): Promise<void> {
    this.loaderStore.show();

    try {
      await lastValueFrom(this.service.postCloseClusterFeeRun(payload));
    } catch (err) {
      Sentry.captureException(err);
      this.setError(err);
    } finally {
      this.loaderStore.hide();
    }
  }
  async loadClusterFeeRunApproved(): Promise<AwaitingClusterFeeRun[]> {
    this.loaderStore.show();
    this.startLoading();
    try {
      const data = await lastValueFrom(this.service.getClusterFeeRunApproved());
      this.state.update((s) => ({ ...s, approvedClusterFees: data }));
      return data;
    } catch (err) {
      Sentry.captureException(err);
      this.setError(err);
      return [];
    } finally {
      this.loaderStore.hide();
      this.stopLoading();
    }
  }

  async approveClusterFeeRun(payload: { batchIds: number[] }): Promise<void> {
    this.loaderStore.show();

    try {
      await lastValueFrom(this.service.approveClusterFeeRun(payload));
    } catch (err) {
      Sentry.captureException(err);
      this.setError(err);
    } finally {
      this.loaderStore.hide();
    }
  }

  async rejectClusterFeeRun(payload: { batchIds: number[] }): Promise<void> {
    this.loaderStore.show();

    try {
      await lastValueFrom(this.service.rejectClusterFeeRun(payload));
    } catch (err) {
      Sentry.captureException(err);
      this.setError(err);
    } finally {
      this.loaderStore.hide();
    }
  }

  // Reset store
  clear(): void {
    this.state.update((s) => ({
      ...s,
      unpaidClusterFeeRun: [],
      awaitingClusterFeeRun: [],
      isLoading: false,
    }));
  }

  private startLoading() {
    this.state.update((s) => ({ ...s, isLoadingCount: s.isLoadingCount + 1 }));
  }

  private stopLoading() {
    this.state.update((s) => ({
      ...s,
      isLoadingCount: Math.max(0, s.isLoadingCount - 1),
    }));
  }

  private setError(err: any) {
    this.state.update((s) => ({
      ...s,
      isLoading: false,
      error: err?.messages,
    }));
  }
}
